import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Phone, Clock } from "lucide-react";
import { track } from "@/lib/analytics";

interface LocalServiceData {
  service: string;
  serviceName: string;
  location: string;
  locationName: string;
  title: string;
  description: string;
  keywords: string;
  heroText: string;
  mapUrl: string;
  features: {
    title: string;
    description: string;
    content: string;
  }[];
  offers: {
    name: string;
    description: string;
    priceRange: string;
  }[];
  projects: {
    area: string;
    specs: string;
    testimonial: string;
    author: string;
  }[];
  ctaText: string;
  ctaDescription: string;
}

interface LocalServiceTemplateProps {
  data: LocalServiceData;
}

export const LocalServiceTemplate: React.FC<LocalServiceTemplateProps> = ({ data }) => {
  const handleQuoteRequest = () => {
    track('quote_request', { service: data.service, location: data.location });
    
    // Store service and location context for pre-filling the form
    localStorage.setItem('quoteContext', JSON.stringify({
      service: data.service,
      serviceName: data.serviceName,
      location: data.location,
      locationName: data.locationName,
      source: `local_page_${data.service}_${data.location}`
    }));
    
    // Navigate to internal form
    window.location.href = '/offerte-snel';
  };

  const localBusinessData = {
    name: `SOL-VI ${data.serviceName} ${data.locationName}`,
    description: `Specialist in ${data.serviceName.toLowerCase()} in ${data.locationName}. Erkend installateur in de regio Gelderland.`,
    url: `https://sol-vi.nl/${data.service}-${data.location}`,
    telephone: "+31 6 55 33 18 70",
    email: "info@sol-vi.nl",
    address: {
      streetAddress: "Hoofdstraat 1",
      addressLocality: "Elst",
      addressRegion: "Gelderland",
      postalCode: "6661 EA",
      addressCountry: "NL"
    },
    openingHours: ["Mo-Fr 08:00-17:00"],
    priceRange: "€€",
    serviceArea: [data.locationName, "Elst", "Gelderland"],
    aggregateRating: {
      ratingValue: 4.9,
      reviewCount: 89
    }
  };

  const serviceData = {
    name: `${data.serviceName} ${data.locationName}`,
    description: data.description,
    provider: "SOL-VI",
    areaServed: [data.locationName],
    serviceType: data.serviceName,
    url: `https://sol-vi.nl/${data.service}-${data.location}`,
    offers: data.offers
  };

  const breadcrumbData = [
    { name: "Home", url: "https://sol-vi.nl" },
    { name: data.serviceName, url: `https://sol-vi.nl/${data.service}` },
    { name: data.locationName, url: `https://sol-vi.nl/${data.service}-${data.location}` }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={data.title}
        description={data.description}
        keywords={data.keywords}
        canonical={`https://sol-vi.nl/${data.service}-${data.location}`}
        type="website"
      />
      
      <LocalBusinessSchema {...localBusinessData} />
      <ServiceSchema {...serviceData} />
      <BreadcrumbSchema items={breadcrumbData} />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-primary">{data.locationName}, Gelderland</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {data.serviceName} {data.locationName}
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  {data.heroText}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" onClick={handleQuoteRequest}>
                    Gratis Offerte {data.locationName}
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31616929110">
                        <Phone className="w-4 h-4 mr-2" />
                        Sven: 06-16929110
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31617105786">
                        <Phone className="w-4 h-4 mr-2" />
                        Nikki: 06-17105786
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Waarom SOL-VI in {data.locationName}?</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                    <span>4.9/5 sterren (89 reviews)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    <span>Installatie binnen 2-4 weken</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span>Lokaal in Gelderland sinds 2019</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3D Map Preview Section */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">
                {data.serviceName} in Uw Buurt - {data.locationName}
              </h2>
              <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
                Bekijk {data.locationName} in 3D en ontdek waar wij {data.serviceName.toLowerCase()} hebben geïnstalleerd. 
                Visualiseer hoe {data.serviceName.toLowerCase()} bij uw woning zouden passen.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Interactieve 3D Weergave</h3>
                  <p className="text-muted-foreground mb-4">
                    Met de 3D viewer kunt u {data.locationName} verkennen en zien waar {data.serviceName.toLowerCase()} 
                    zijn geïnstalleerd. Dit geeft u een realistisch beeld van de mogelijkheden 
                    voor uw eigen woning.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Bekijk lokale installaties</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Verken verschillende woningtypes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Visualiseer mogelijkheden voor uw woning</span>
                    </div>
                  </div>
                </div>
                
                <Button 
                  size="lg" 
                  onClick={() => window.open(data.mapUrl, '_blank')}
                  className="w-full sm:w-auto"
                >
                  🏠 Bekijk {data.locationName} in 3D
                </Button>
              </div>
              
              <div className="relative">
                <div className="aspect-video rounded-lg overflow-hidden shadow-lg border">
                  <iframe
                    src={data.mapUrl}
                    width="100%"
                    height="100%"
                    title={`3D weergave ${data.locationName} - ${data.serviceName} locaties`}
                    className="border-0"
                    loading="lazy"
                    allowFullScreen
                  />
                </div>
                <div className="absolute top-2 right-2 bg-background/90 px-2 py-1 rounded text-xs text-muted-foreground">
                  Interactieve 3D viewer
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">
                💡 <strong>Tip:</strong> Gebruik de 3D viewer om uw eigen woning te vinden en te zien 
                hoe andere woningen in {data.locationName} zijn uitgerust met {data.serviceName.toLowerCase()}. 
                Klik en sleep om te navigeren, scroll om in te zoomen.
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Waarom Kiezen voor {data.serviceName} in {data.locationName}?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {data.features.map((feature, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{feature.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Local Projects Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Projecten in {data.locationName}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {data.projects.map((project, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{project.area}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{project.specs}</p>
                    <p className="text-sm">"{project.testimonial}" - {project.author}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">{data.ctaText}</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {data.ctaDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={handleQuoteRequest}>
                Gratis Offerte Aanvragen
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/contact">Contact Opnemen</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default LocalServiceTemplate;